import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/shared/SectionCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuthStore } from "@/features/auth/store";
import { apiErrorMessage } from "@/lib/api/errors";
import { cn } from "@/lib/utils";

import { useMfaDisable, useMfaEnable, useMfaSetup } from "../hooks";
import type { MfaSetupResponse } from "../types";

/** Carte « Double authentification » : activation (QR + code) / désactivation. */
export function TwoFactorCard() {
  const me = useAuthStore((s) => s.me);
  const setup = useMfaSetup();
  const enable = useMfaEnable();
  const disable = useMfaDisable();

  const [qr, setQr] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState("");

  if (!me) return null;

  const startSetup = () =>
    setup.mutate(undefined, {
      onSuccess: (data) => setQr(data),
      onError: (e) => toast.error(apiErrorMessage(e, "Impossible de démarrer l’activation.")),
    });

  const confirmEnable = (e: React.FormEvent) => {
    e.preventDefault();
    enable.mutate(code.trim(), {
      onSuccess: (data) => {
        setBackupCodes(data.backup_codes);
        setQr(null);
        setCode("");
        toast.success("Double authentification activée.");
      },
      onError: (err) => toast.error(apiErrorMessage(err, "Opération impossible.")),
    });
  };

  const confirmDisable = (e: React.FormEvent) => {
    e.preventDefault();
    disable.mutate(password, {
      onSuccess: () => {
        setPassword("");
        toast.success("Double authentification désactivée.");
      },
      onError: (err) => toast.error(apiErrorMessage(err, "Opération impossible.")),
    });
  };

  return (
    <SectionCard
      title="Double authentification (2FA)"
      icon={ShieldCheck}
      bodyClassName="space-y-4"
      actions={
        <span
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium",
            me.mfa_enabled ? "bg-success/12 text-success" : "bg-warning/14 text-warning",
          )}
        >
          {me.mfa_enabled ? (
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ShieldOff className="h-3.5 w-3.5" aria-hidden />
          )}
          {me.mfa_enabled ? "Activée" : "Désactivée"}
        </span>
      }
    >
        {/* Codes de secours affichés une seule fois après activation. */}
        {backupCodes && (
          <div className="border border-warning/40 bg-warning/5 p-4">
            <p className="mb-2 text-sm font-medium">
              Conservez ces codes de secours — ils ne seront plus affichés.
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {backupCodes.map((c) => (
                <span key={c} className="bg-background px-2 py-1 text-center">
                  {c}
                </span>
              ))}
            </div>
            <Button className="mt-3" size="sm" variant="outline" onClick={() => setBackupCodes(null)}>
              J'ai noté mes codes
            </Button>
          </div>
        )}

        {!backupCodes && me.mfa_enabled && (
          <form onSubmit={confirmDisable} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              La 2FA est <strong className="text-success">activée</strong>. Pour la désactiver,
              confirmez votre mot de passe.
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="mfa-pwd">Mot de passe</Label>
                <PasswordInput
                  id="mfa-pwd"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" variant="destructive" disabled={disable.isPending || !password}>
                {disable.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                <ShieldOff className="h-4 w-4" /> Désactiver
              </Button>
            </div>
          </form>
        )}

        {!backupCodes && !me.mfa_enabled && !qr && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Ajoutez une couche de sécurité : un code temporaire sera demandé à chaque connexion,
              en plus de votre mot de passe.
            </p>
            <Button onClick={startSetup} disabled={setup.isPending}>
              {setup.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Activer la 2FA
            </Button>
          </div>
        )}

        {!backupCodes && !me.mfa_enabled && qr && (
          <form onSubmit={confirmEnable} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Scannez ce QR code avec Google Authenticator, Authy ou équivalent, puis saisissez le
              code à 6 chiffres généré.
            </p>
            <img
              src={qr.qr_data_uri}
              alt="QR code 2FA"
              className="mx-auto h-44 w-44 border border-border bg-white p-2"
            />
            <p className="text-center font-mono text-xs text-muted">
              Clé manuelle : {qr.secret}
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="mfa-code">Code de vérification</Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={enable.isPending || code.trim().length < 6}>
                {enable.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirmer
              </Button>
            </div>
          </form>
        )}
    </SectionCard>
  );
}
